import React, { useState, useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { TrendingUp, BarChart3, Activity, DollarSign } from 'lucide-react';

const ContractsEvolutionChart = ({ statistics, isLoading }) => {
  const [chartType, setChartType] = useState('area'); // 'area', 'line', 'bar'
  const [metric, setMetric] = useState('contracts'); // 'contracts', 'revenue'

  // Transform monthly statistics data
  const chartData = useMemo(() => {
    console.log("statistics in chart:", statistics);
    if (!statistics?.monthlyStatistics) return [];

    return statistics.monthlyStatistics
      .slice()
      .reverse() // Reverse to show oldest to newest
      .map(item => ({
        month: new Date(item.month).toLocaleDateString('ro-RO', { 
          month: 'short',
          year: '2-digit'
        }),
        contracte: item.totalContracts,
        semnate: item.signedContracts,
        venit: Math.round(item.totalRevenue),
        venitFormatat: new Intl.NumberFormat('ro-RO', {
          style: 'currency',
          currency: 'RON',
          minimumFractionDigits: 0
        }).format(item.totalRevenue)
      }));
  }, [statistics]);

  // Calculate trends
  const trend = useMemo(() => {
    if (chartData.length < 2) return { percentage: 0, isPositive: true };

    const current = metric === 'contracts' 
      ? chartData[chartData.length - 1]?.contracte 
      : chartData[chartData.length - 1]?.venit;
    const previous = metric === 'contracts'
      ? chartData[chartData.length - 2]?.contracte
      : chartData[chartData.length - 2]?.venit;

    if (!previous || previous === 0) return { percentage: 0, isPositive: true };

    const percentage = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(percentage).toFixed(1),
      isPositive: percentage >= 0
    };
  }, [chartData, metric]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center">
              <span 
                className="w-3 h-3 rounded-full mr-2" 
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}:
            </span>
            <span className="font-semibold">
              {entry.name === 'venit' ? entry.payload.venitFormatat : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    if (metric === 'contracts') {
      if (chartType === 'area') {
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorContracte" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="colorSemnate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
              iconType="circle"
            />
            <Area 
              type="monotone" 
              dataKey="contracte" 
              name="Total Contracte"
              stroke="#3B82F6" 
              fillOpacity={1}
              fill="url(#colorContracte)"
              strokeWidth={2}
            />
            <Area 
              type="monotone" 
              dataKey="semnate" 
              name="Contracte Semnate"
              stroke="#10B981" 
              fillOpacity={1}
              fill="url(#colorSemnate)"
              strokeWidth={2}
            />
          </AreaChart>
        );
      } else if (chartType === 'line') {
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
              iconType="circle"
            />
            <Line 
              type="monotone" 
              dataKey="contracte" 
              name="Total Contracte"
              stroke="#3B82F6" 
              strokeWidth={3}
              dot={{ fill: '#3B82F6', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="semnate" 
              name="Contracte Semnate"
              stroke="#10B981" 
              strokeWidth={3}
              dot={{ fill: '#10B981', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        );
      } else {
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
              iconType="rect"
            />
            <Bar 
              dataKey="contracte" 
              name="Total Contracte"
              fill="#3B82F6"
              radius={[8, 8, 0, 0]}
            />
            <Bar 
              dataKey="semnate" 
              name="Contracte Semnate"
              fill="#10B981"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        );
      }
    } else {
      // Revenue chart
      if (chartType === 'area') {
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorVenit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `${Math.round(value / 1000)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
              iconType="circle"
            />
            <Area 
              type="monotone" 
              dataKey="venit" 
              name="Venit"
              stroke="#8B5CF6" 
              fillOpacity={1}
              fill="url(#colorVenit)"
              strokeWidth={2}
            />
          </AreaChart>
        );
      } else if (chartType === 'line') {
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `${Math.round(value / 1000)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
              iconType="circle"
            />
            <Line 
              type="monotone" 
              dataKey="venit" 
              name="Venit"
              stroke="#8B5CF6" 
              strokeWidth={3}
              dot={{ fill: '#8B5CF6', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        );
      } else {
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `${Math.round(value / 1000)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
              iconType="rect"
            />
            <Bar 
              dataKey="venit" 
              name="Venit"
              fill="#8B5CF6"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        );
      }
    }
  };

  if (isLoading) {
    return (
      <div className="h-64 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg flex items-center justify-center animate-pulse">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-blue-400 mx-auto mb-4 animate-bounce" />
          <p className="text-gray-600 font-medium">Se încarcă graficul...</p>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-64 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg flex items-center justify-center border-2 border-dashed border-blue-200">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Nu există date disponibile</p>
          <p className="text-sm text-gray-500 mt-2">
            Datele vor apărea după crearea contractelor
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Metric selector */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setMetric('contracts')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              metric === 'contracts'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Activity className="w-4 h-4 inline mr-2" />
            Contracte
          </button>
          <button
            onClick={() => setMetric('revenue')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              metric === 'revenue'
                ? 'bg-white text-purple-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Venit
          </button>
        </div>

        {/* Chart type selector */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setChartType('area')}
            className={`p-2 rounded-lg transition-all ${
              chartType === 'area'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Grafic cu arie"
          >
            <Activity className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('line')}
            className={`p-2 rounded-lg transition-all ${
              chartType === 'line'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Grafic cu linii"
          >
            <TrendingUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`p-2 rounded-lg transition-all ${
              chartType === 'bar'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Grafic cu bare"
          >
            <BarChart3 className="w-4 h-4" />
          </button>
        </div>

        {/* Trend indicator */}
        <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg ${
          trend.isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          <TrendingUp 
            className={`w-4 h-4 ${trend.isPositive ? '' : 'transform rotate-180'}`} 
          />
          <span className="text-sm font-semibold">
            {trend.isPositive ? '+' : '-'}{trend.percentage}%
          </span>
          <span className="text-xs text-gray-500">vs. luna trecută</span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default ContractsEvolutionChart;